import { diffArrays } from 'diff';
import { sanitizeLegalHtml } from 'utils/legalHtmlSanitizer';
import DOMPurify from 'dompurify';

/**
 * Produces an inline "track-changes" redline between two legal-page HTML
 * documents: the currently published version (`oldHtml`) and the draft under
 * review (`newHtml`). Additions are wrapped in <ins>, deletions in <del>, so a
 * single rendered .Legal document reads top-to-bottom with the edits woven in.
 *
 * The diff is HTML-structure aware but deliberately coarse: it aligns top-level
 * block elements (p, h1-6, ul/ol, table, blockquote, ...) by their text, runs an
 * HTML-token-aware inline diff inside changed text blocks (preserving inline
 * formatting such as bold, links, and `sup` in the new content), descends one
 * level into lists to highlight changed list items, and keeps tables at row
 * granularity (whole rows added/removed, no intra-cell churn). The assembled
 * markup is always passed through sanitizeLegalHtml before being returned.
 */

const LIST_TAGS = new Set(['UL', 'OL']);

type DiffOp =
  | { type: 'equal'; oldEl: Element; newEl: Element }
  | { type: 'changed'; oldEl: Element; newEl: Element }
  | { type: 'removed'; el: Element }
  | { type: 'added'; el: Element };

const normalizeText = (el: Element): string => (el.textContent ?? '').replace(/\s+/g, ' ').trim();

// A block's identity for sequence alignment: same tag + same visible text.
const signatureOf = (el: Element): string => `${el.tagName}\u0000${normalizeText(el)}`;

/**
 * Aligns two sequences of elements by signature and pairs each run of removed
 * elements with the run of added elements that immediately follows it, so a
 * replaced block surfaces as a single "changed" op (word-diffable) rather than
 * an unrelated delete + insert.
 */
const buildOps = (oldEls: readonly Element[], newEls: readonly Element[]): DiffOp[] => {
  const parts = diffArrays(oldEls.map(signatureOf), newEls.map(signatureOf));
  const ops: DiffOp[] = [];
  let oldIndex = 0;
  let newIndex = 0;

  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    const length = part.value.length;

    if (!part.added && !part.removed) {
      for (let k = 0; k < length; k += 1) {
        ops.push({ type: 'equal', oldEl: oldEls[oldIndex++], newEl: newEls[newIndex++] });
      }
      continue;
    }

    if (part.removed) {
      const next = parts[i + 1];
      if (next?.added) {
        const addedLength = next.value.length;
        const paired = Math.min(length, addedLength);
        for (let k = 0; k < paired; k += 1) {
          ops.push({ type: 'changed', oldEl: oldEls[oldIndex++], newEl: newEls[newIndex++] });
        }
        for (let k = paired; k < length; k += 1) {
          ops.push({ type: 'removed', el: oldEls[oldIndex++] });
        }
        for (let k = paired; k < addedLength; k += 1) {
          ops.push({ type: 'added', el: newEls[newIndex++] });
        }
        i += 1; // the following added part has been consumed
        continue;
      }
      for (let k = 0; k < length; k += 1) {
        ops.push({ type: 'removed', el: oldEls[oldIndex++] });
      }
      continue;
    }

    for (let k = 0; k < length; k += 1) {
      ops.push({ type: 'added', el: newEls[newIndex++] });
    }
  }

  return ops;
};

const isTagToken = (token: string): boolean => /^<[^>]+>$/.test(token);

/**
 * Splits an element's `innerHTML` into atomic tokens: whole tags (`<a ...>`,
 * `</a>`, `<br>`) and the word / whitespace runs of the text between them. The
 * text is left HTML-escaped exactly as `innerHTML` produced it, so tokens can
 * be re-emitted verbatim without double-escaping entities.
 */
const tokenizeInlineHtml = (html: string): string[] => {
  const tokens: string[] = [];
  const pattern = /<[^>]+>|[^<]+/g;
  let match: RegExpExecArray | null = pattern.exec(html);
  while (match !== null) {
    const chunk = match[0];
    if (chunk.startsWith('<')) {
      tokens.push(chunk);
    } else {
      const runs = chunk.match(/\S+|\s+/g);
      if (runs) {
        tokens.push(...runs);
      }
    }
    match = pattern.exec(html);
  }
  return tokens;
};

const wrapTextRun = (text: string, status: 'added' | 'removed' | 'equal'): string => {
  if (!text) {
    return '';
  }
  if (status === 'added') {
    return `<ins class="diff-ins">${text}</ins>`;
  }
  if (status === 'removed') {
    return `<del class="diff-del">${text}</del>`;
  }
  return text;
};

/**
 * HTML-aware inline redline of two block bodies. Tags are aligned as opaque
 * tokens and re-emitted as-is so inline formatting (bold, links, `sup`) in the
 * new content survives; only prose is wrapped in <ins>/<del>. Removed tags are
 * dropped (their text stays, wrapped in <del>) so deletions never leave
 * dangling markup.
 */
const inlineHtmlDiff = (oldHtml: string, newHtml: string): string => {
  const parts = diffArrays(tokenizeInlineHtml(oldHtml), tokenizeInlineHtml(newHtml));
  let out = '';
  parts.forEach((part) => {
    const status: 'added' | 'removed' | 'equal' = part.added
      ? 'added'
      : part.removed
        ? 'removed'
        : 'equal';
    let textRun = '';
    part.value.forEach((token) => {
      if (isTagToken(token)) {
        out += wrapTextRun(textRun, status);
        textRun = '';
        if (status !== 'removed') {
          out += token;
        }
      } else {
        textRun += token;
      }
    });
    out += wrapTextRun(textRun, status);
  });
  return out;
};

// Wraps a whole block element as an addition/deletion, kept block-level via CSS.
const blockWrap = (tag: 'ins' | 'del', el: Element): string =>
  `<${tag} class="diff-block">${el.outerHTML}</${tag}>`;

const cloneShallow = (el: Element): HTMLElement => el.cloneNode(false) as HTMLElement;

const childListItems = (el: Element): Element[] =>
  Array.from(el.children).filter((child) => child.tagName === 'LI');

const tableRows = (el: Element): Element[] => Array.from(el.querySelectorAll('tr'));

// List items: word-diff changed items, wrap added/removed item contents.
const renderListItems = (oldLis: readonly Element[], newLis: readonly Element[]): string =>
  buildOps(oldLis, newLis)
    .map((op) => {
      if (op.type === 'equal') {
        return op.newEl.outerHTML;
      }
      if (op.type === 'changed') {
        const li = cloneShallow(op.newEl);
        li.innerHTML = inlineHtmlDiff(op.oldEl.innerHTML, op.newEl.innerHTML);
        return li.outerHTML;
      }
      const li = cloneShallow(op.el);
      const tag = op.type === 'removed' ? 'del' : 'ins';
      const cleanInnerHtml = DOMPurify.sanitize(op.el.innerHTML);
      li.innerHTML = `<${tag} class="diff-block">${cleanInnerHtml}</${tag}>`;
      return li.outerHTML;
    })
    .join('');

// Tables stay coarse: rows are added/removed/replaced whole, marked via a class.
const markRow = (tr: Element, className: string): string => {
  const row = tr.cloneNode(true) as HTMLElement;
  row.classList.add(className);
  return row.outerHTML;
};

const renderRows = (oldRows: readonly Element[], newRows: readonly Element[]): string =>
  buildOps(oldRows, newRows)
    .map((op) => {
      switch (op.type) {
        case 'equal':
          return op.newEl.outerHTML;
        case 'changed':
          return markRow(op.oldEl, 'diff-del') + markRow(op.newEl, 'diff-ins');
        case 'removed':
          return markRow(op.el, 'diff-del');
        case 'added':
        default:
          return markRow(op.el, 'diff-ins');
      }
    })
    .join('');

const renderChangedBlock = (oldEl: Element, newEl: Element): string => {
  if (oldEl.tagName !== newEl.tagName) {
    return blockWrap('del', oldEl) + blockWrap('ins', newEl);
  }
  if (LIST_TAGS.has(newEl.tagName)) {
    const list = cloneShallow(newEl);
    list.innerHTML = renderListItems(childListItems(oldEl), childListItems(newEl));
    return list.outerHTML;
  }
  if (newEl.tagName === 'TABLE') {
    const table = cloneShallow(newEl);
    table.innerHTML = `<tbody>${renderRows(tableRows(oldEl), tableRows(newEl))}</tbody>`;
    return table.outerHTML;
  }
  const block = cloneShallow(newEl);
  block.innerHTML = inlineHtmlDiff(oldEl.innerHTML, newEl.innerHTML);
  return block.outerHTML;
};

const renderTopLevelOp = (op: DiffOp): string => {
  switch (op.type) {
    case 'equal':
      return op.newEl.outerHTML;
    case 'changed':
      return renderChangedBlock(op.oldEl, op.newEl);
    case 'removed':
      return blockWrap('del', op.el);
    case 'added':
    default:
      return blockWrap('ins', op.el);
  }
};

/**
 * Returns an inline redline of `newHtml` against `oldHtml`, sanitized for
 * rendering inside a `.Legal` container. When there is no published baseline
 * (`oldHtml` empty), the draft is returned sanitized with no diff markup.
 */
export const diffLegalHtml = (oldHtml: string, newHtml: string): string => {
  if (!oldHtml || !oldHtml.trim()) {
    return sanitizeLegalHtml(newHtml ?? '');
  }

  const parser = new DOMParser();
  const oldBlocks = Array.from(parser.parseFromString(oldHtml, 'text/html').body.children);
  const newBlocks = Array.from(parser.parseFromString(newHtml ?? '', 'text/html').body.children);

  const html = buildOps(oldBlocks, newBlocks).map(renderTopLevelOp).join('');
  return sanitizeLegalHtml(html);
};

/**
 * Whether the redline between `oldHtml` and `newHtml` would show any change,
 * detected by the same insert/delete/block markers the rendered redline uses.
 * This is intentionally consistent with {@link diffLegalHtml}: a formatting-only
 * edit inside a block whose visible text is unchanged aligns as "equal" and is
 * therefore reported as no change, matching what a reviewer sees. Used to flag a
 * draft that is identical to the currently published version (an empty redline).
 */
export const legalHtmlHasChanges = (oldHtml: string, newHtml: string): boolean =>
  /class="diff-(ins|del|block)"/.test(diffLegalHtml(oldHtml, newHtml));
